/**
 * Los tres niveles de selección de una plantilla de WhatsApp, en un módulo PURO.
 *
 * Vive aparte de `Campanas.jsx` porque la regla que importa —qué se puede elegir y
 * qué no— tiene que poder probarse sin montar el wizard entero, y porque el arnés
 * asserta sobre el NIVEL, no sobre el texto que se pinte.
 *
 * El estado no lo decidimos nosotros: lo decide Meta y la lista crece. Por eso el
 * mapa es explícito y todo lo que NO esté en él cae a BLOQUEADO. Un estado nuevo que
 * se colara como seleccionable ofrecería un envío que falla contra Meta; uno que se
 * cuele como bloqueado sólo esconde una plantilla hasta que alguien añada la entrada.
 * El fallo seguro es el segundo.
 */

export const NIVEL = {
  SELECCIONABLE: 'seleccionable',
  AVISO: 'aviso',
  BLOQUEADO: 'bloqueado',
};

const POR_ESTADO = {
  APPROVED: NIVEL.SELECCIONABLE,

  // Usable, pero Meta la marcó por feedback negativo: si no mejora pasa a PAUSED.
  // Se avisa, no se bloquea — sigue siendo enviable hoy.
  FLAGGED: NIVEL.AVISO,

  PENDING: NIVEL.BLOQUEADO,
  REJECTED: NIVEL.BLOQUEADO,
  PAUSED: NIVEL.BLOQUEADO,
  DISABLED: NIVEL.BLOQUEADO,
};

/**
 * Categorías que NO se pueden enviar desde campañas, sea cual sea su estado.
 *
 * AUTHENTICATION es un código de un solo uso: transaccional, uno a uno, disparado por
 * un acto del usuario en ese mismo instante. Un envío MASIVO de códigos de
 * verificación no es un caso de uso legítimo — o es un error de configuración, o es un
 * ataque. Por eso no se gatea con consentimiento, se prohíbe: con la otra forma, un
 * cliente que consiguiera consentimiento explícito podría lanzar mil OTPs.
 *
 * Esto NO es un estado nuevo: es un cuarto motivo de no-seleccionable, y por eso el
 * motivo viaja aparte del nivel. «No enviable desde Campañas» no es lo mismo que «en
 * revisión», y el usuario tiene que poder distinguirlos.
 */
const CATEGORIAS_NO_ENVIABLES = new Set(['AUTHENTICATION']);

/**
 * Una MARKETING sin vía de baja no se puede elegir para una campaña.
 *
 * El art. 28 B de la Ley 19.496 exige que una comunicación promocional indique una
 * forma expedita de pedir que dejen de enviarla. Las plantillas que PUX crea lo llevan
 * en un FOOTER desde `plantillaDesdeFormulario`, pero **la lista incluye plantillas que
 * PUX no creó** —un cliente puede crearlas desde Business Manager— y son justo las que
 * no lo llevan.
 *
 * MEDIDO el 28-sep-2026 sobre la caché real: de 7 MARKETING aprobadas, **5 no tienen
 * FOOTER** (`retomar_conversacion` es_MX y pt_BR en PUX y en Brokers, y
 * `seguimiento_solicitud` pt_BR en PUX). Y ninguna de las cinco lleva la baja en el
 * BODY: se comprobó con un regex de siete formas y su control positivo, por si el
 * discriminador «sin FOOTER» producía un bloqueo falso. No lo produce.
 *
 * ⚠️ ESTO NO ES EL GATE, y aquí importa más que en los otros dos motivos. El que corta
 * es `verificarFooterBaja` en el servidor, que mira la plantilla VIVA en Meta en el
 * momento de enviar. Esto es un aviso temprano: sin él el operador monta la campaña
 * entera y se entera al pulsar enviar, con el trabajo ya hecho.
 */
const MOTIVO_SIN_VIA_DE_BAJA = 'sin_via_de_baja';

export const MOTIVO_BLOQUEO = {
  ESTADO: 'estado',
  CATEGORIA: 'categoria',
  SIN_VIA_DE_BAJA: MOTIVO_SIN_VIA_DE_BAJA,
};

/**
 * ¿Le falta la vía de baja a esta plantilla de marketing?
 *
 * Devuelve `false` cuando NO SE PUEDE SABER —`components` ausente o no es un array—, y
 * eso es deliberado: un llamante que no tiene los componentes no puede concluir que
 * falten. La dirección segura aquí es la contraria que en la categoría, porque el
 * servidor SÍ lo comprueba: un falso «bloqueada» esconde una plantilla legítima y no
 * protege de nada, mientras que un falso «elegible» sólo llega hasta el gate real.
 *
 * Un FOOTER vacío o de espacios no cuenta — la misma comprobación que hace
 * `verificarFooterBaja`, para que la UI y el servidor no discrepen por un detalle.
 */
export function faltaViaDeBaja(categoria, components) {
  if (typeof categoria !== 'string' || categoria.trim().toUpperCase() !== 'MARKETING') return false;
  if (!Array.isArray(components)) return false;
  return !components.some(
    (c) => c?.type === 'FOOTER' && typeof c.text === 'string' && c.text.trim(),
  );
}

/**
 * El nivel de una plantilla. Cualquier estado desconocido, nulo o raro → BLOQUEADO.
 *
 * La CATEGORÍA es opcional a propósito: hay llamantes que sólo tienen el estado, y
 * omitirla no puede volver permisiva la función. Cuando se pasa, una categoría
 * prohibida manda sobre el estado — una AUTHENTICATION aprobada sigue sin poder
 * enviarse.
 *
 * ESTO NO ES EL GATE. El backend vuelve a comprobarlo en `executeCampaign`: bloquear
 * sólo aquí sería un gate cosmético, la lección del `organizationId` del cuerpo.
 */
export function nivelDePlantilla(status, categoria, components) {
  if (typeof categoria === 'string' && CATEGORIAS_NO_ENVIABLES.has(categoria.trim().toUpperCase())) {
    return NIVEL.BLOQUEADO;
  }
  if (faltaViaDeBaja(categoria, components)) return NIVEL.BLOQUEADO;
  if (typeof status !== 'string') return NIVEL.BLOQUEADO;
  return POR_ESTADO[status.trim().toUpperCase()] || NIVEL.BLOQUEADO;
}

/**
 * Por QUÉ está bloqueada, para poder decirlo. `null` si no lo está.
 *
 * ⚠️ QUÉ ORDEN IMPORTA AQUÍ Y CUÁL NO — medido, no razonado.
 *
 * **El de AUTHENTICATION frente al pie NO importa, y decir lo contrario era falso.** Lo
 * destapó una mutación que invirtió los dos y pasó los 52 casos: `faltaViaDeBaja` sólo
 * devuelve true para MARKETING y `CATEGORIAS_NO_ENVIABLES` sólo contiene AUTHENTICATION,
 * así que **ninguna categoría activa las dos condiciones** y el orden es inobservable.
 * Se comprobó de forma exhaustiva sobre el vocabulario y su basura, con control positivo
 * de que cada condición se dispara por separado.
 *
 * Eso deja de ser cierto en cuanto alguien meta MARKETING en `CATEGORIAS_NO_ENVIABLES`,
 * y entonces el orden pasa a ser una decisión de verdad. Por eso el test que lo cubre
 * afirma la **disyunción**, no el orden: si dejan de ser disjuntas, avisa — comprobado,
 * esa mutación pone 6 casos en rojo.
 *
 * **El que SÍ importa es el del pie frente al estado.** `ESTADO` es el retorno por
 * defecto, así que el pie tiene que comprobarse antes o una MARKETING aprobada sin pie
 * diría «Aprobada» — y una razón que no explica nada manda al operador a esperar cuando
 * lo que hay que hacer es crear la plantilla otra vez.
 */
export function motivoDeBloqueo(status, categoria, components) {
  if (nivelDePlantilla(status, categoria, components) !== NIVEL.BLOQUEADO) return null;
  if (typeof categoria === 'string' && CATEGORIAS_NO_ENVIABLES.has(categoria.trim().toUpperCase())) {
    return MOTIVO_BLOQUEO.CATEGORIA;
  }
  if (faltaViaDeBaja(categoria, components)) return MOTIVO_BLOQUEO.SIN_VIA_DE_BAJA;
  return MOTIVO_BLOQUEO.ESTADO;
}

/** `true` si la plantilla se puede elegir para una campaña (con o sin aviso). */
export function esSeleccionable(status, categoria, components) {
  return nivelDePlantilla(status, categoria, components) !== NIVEL.BLOQUEADO;
}

/**
 * El motivo del rechazo, sólo si dice algo.
 *
 * Graph devuelve la cadena `'NONE'` en las plantillas que no fueron rechazadas —
 * medido contra la WABA real: las cuatro APPROVED traen `rejected_reason: 'NONE'`.
 * Pintarlo tal cual mostraría «Motivo: NONE» bajo una plantilla aprobada.
 */
export function motivoDeRechazo(tpl) {
  const motivo = tpl?.rejected_reason;
  if (typeof motivo !== 'string') return null;
  const limpio = motivo.trim();
  if (!limpio || limpio.toUpperCase() === 'NONE') return null;
  return limpio;
}

/**
 * La puntuación de calidad como cadena corta, o `null`.
 *
 * Graph la manda como OBJETO `{score, date}`, no como cadena. `UNKNOWN` es el valor
 * que trae una plantilla sin tráfico suficiente y no aporta nada al usuario.
 */
export function puntuacionDeCalidad(tpl) {
  const score = tpl?.quality_score?.score;
  if (typeof score !== 'string') return null;
  const limpio = score.trim().toUpperCase();
  if (!limpio || limpio === 'UNKNOWN') return null;
  return limpio;
}
